import {
  WaterLocomotion, classifyWaterLocomotion, sampleWorldWaterAt, stepPlayerLocomotion,
  PLAYER_COMMAND_SECONDS, PLAYER_SAVED_COMMAND_LIMIT, PlayerMotionMode, PlayerStance, PlayerGait,
  type ClientInput, type PlayerLocomotionInput, type PlayerLocomotionOptions, type PlayerLocomotionState,
  type PackedPlayer,
} from "@endclock/shared";

export const PREDICTION_TINY_ERROR = 0.002;
export const PREDICTION_HARD_ERROR = 3;
export const PREDICTION_OFFSET_DECAY = 16;

/** Simulation restores authority exactly; all smoothing lives outside that state. */
export class PlayerPrediction {
  readonly state: PlayerLocomotionState;
  readonly renderOffset = { x: 0, y: 0, z: 0 };
  error = 0;
  corrections = 0;
  reconciliations = 0;
  maximumPending = 0;
  replayedCommands = 0;
  historyOverflows = 0;
  private lastTick = -1;
  private generation = -1;
  private lastSequence = 0;
  private overflow = false;
  private readonly saved: ClientInput[] = [];
  private readonly previewState: PlayerLocomotionState;

  constructor(x: number, z: number) {
    this.state = { x, y: sampleWorldWaterAt(x, z).terrainY, z,
      velocityX: 0, velocityY: 0, velocityZ: 0, yaw: 0, locomotion: WaterLocomotion.Ground, wetness: 0,
      aimYaw: 0, aimPitch: 0, stance: PlayerStance.Standing, gait: PlayerGait.Walk,
      motionMode: PlayerMotionMode.Active, ragdollEvent: 0, motionStartTick: 0,
      impulseX: 0, impulseZ: 0, getUpVariant: 0, motionEpoch: 0 };
    this.previewState = { ...this.state };
  }

  get pendingCount(): number { return this.saved.length; }
  get pendingSequences(): readonly number[] { return this.saved.map((command) => command.s); }
  get renderX(): number { return this.previewState.x + this.renderOffset.x; }
  get renderY(): number { return this.previewState.y + this.renderOffset.y; }
  get renderZ(): number { return this.previewState.z + this.renderOffset.z; }

  predict(command: ClientInput, options: PlayerLocomotionOptions): void {
    if (command.s <= this.lastSequence) return;
    this.lastSequence = command.s;
    if (this.overflow) return;
    if (this.saved.length >= PLAYER_SAVED_COMMAND_LIMIT) {
      this.saved.length = 0; this.overflow = true; this.historyOverflows += 1; return;
    }
    this.saved.push({ ...command });
    this.maximumPending = Math.max(this.maximumPending, this.saved.length);
    this.apply(command, options);
    Object.assign(this.previewState, this.state);
  }

  /** Compatibility for callers testing the shared step; gameplay uses predict. */
  step(input: PlayerLocomotionInput, delta: number, options: PlayerLocomotionOptions): void {
    stepPlayerLocomotion(this.state, input, delta, options);
    Object.assign(this.previewState, this.state);
  }

  preview(input: PlayerLocomotionInput, fractionSeconds: number, options: PlayerLocomotionOptions): void {
    Object.assign(this.previewState, this.state);
    if (this.state.motionMode === PlayerMotionMode.Active && !this.overflow) {
      stepPlayerLocomotion(this.previewState, input, Math.max(0, Math.min(PLAYER_COMMAND_SECONDS, fractionSeconds)), options);
    }
  }

  updateRender(delta: number): void {
    const damping = Math.exp(-PREDICTION_OFFSET_DECAY * Math.max(0, Math.min(0.25, delta)));
    this.renderOffset.x *= damping; this.renderOffset.y *= damping; this.renderOffset.z *= damping;
  }

  reconcile(authority: readonly [...PackedPlayer, ...unknown[]], tick: number, generation: number, options: PlayerLocomotionOptions): boolean;
  reconcile(authority: readonly [...PackedPlayer, ...unknown[]], tick: number, generation: number, ageSeconds: number,
    input: PlayerLocomotionInput, options: PlayerLocomotionOptions): boolean;
  reconcile(authority: readonly [...PackedPlayer, ...unknown[]], tick: number, generation: number,
    optionsOrAge: PlayerLocomotionOptions | number, _input?: PlayerLocomotionInput, oldOptions?: PlayerLocomotionOptions): boolean {
    const options = typeof optionsOrAge === "number" ? oldOptions! : optionsOrAge;
    if (this.generation === generation && tick <= this.lastTick) return false;
    const fresh = this.generation !== generation;
    const epochChanged = (this.state.motionEpoch ?? 0) !== authority[21];
    const motionChanged = (this.state.motionMode ?? 0) !== authority[15];
    const oldX = this.state.x, oldY = this.state.y, oldZ = this.state.z;
    this.generation = generation; this.lastTick = tick;
    if (fresh || epochChanged || this.overflow) {
      this.saved.length = 0; this.lastSequence = authority[5]; this.overflow = false;
    } else {
      while (this.saved.length && this.saved[0]!.s <= authority[5]) this.saved.shift();
    }
    Object.assign(this.state, {
      x: authority[1], y: authority[2], z: authority[3], yaw: authority[4],
      locomotion: authority[6], wetness: authority[7], velocityX: authority[8], velocityY: authority[9], velocityZ: authority[10],
      aimYaw: authority[11], aimPitch: authority[12], stance: authority[13], gait: authority[14],
      motionMode: authority[15], ragdollEvent: authority[16], motionStartTick: authority[17],
      impulseX: authority[18], impulseZ: authority[19], getUpVariant: authority[20], motionEpoch: authority[21],
    });
    for (const command of this.saved) { this.apply(command, options); this.replayedCommands += 1; }
    this.error = Math.hypot(this.state.x - oldX, this.state.y - oldY, this.state.z - oldZ);
    this.reconciliations += 1;
    const hard = fresh || epochChanged || this.error > PREDICTION_HARD_ERROR * Math.max(1, options.speedMultiplier ?? 1);
    if (hard) {
      this.renderOffset.x = 0; this.renderOffset.y = 0; this.renderOffset.z = 0;
      if (!fresh) this.corrections += 1;
    } else if (this.error > PREDICTION_TINY_ERROR || motionChanged) {
      this.renderOffset.x += oldX - this.state.x; this.renderOffset.y += oldY - this.state.y; this.renderOffset.z += oldZ - this.state.z;
    }
    Object.assign(this.previewState, this.state);
    return hard;
  }

  teleport(x: number, y: number, z: number): void {
    Object.assign(this.state, { x, y, z, velocityX: 0, velocityY: 0, velocityZ: 0,
      locomotion: classifyWaterLocomotion(sampleWorldWaterAt(x, z).depth), motionMode: PlayerMotionMode.Active, motionEpoch: (this.state.motionEpoch ?? 0) + 1 });
    this.saved.length = 0; this.overflow = false;
    this.renderOffset.x = 0; this.renderOffset.y = 0; this.renderOffset.z = 0;
    Object.assign(this.previewState, this.state);
  }

  private apply(command: ClientInput, options: PlayerLocomotionOptions): void {
    if (this.state.motionMode !== PlayerMotionMode.Active) return;
    stepPlayerLocomotion(this.state, { ...command, run: command.run === 1 }, PLAYER_COMMAND_SECONDS, options);
  }
}
